import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOrganization } from "../api.js";
import { Button } from "../components/ui/button.jsx";
import { ArrowLeft } from "lucide-react";

export default function OrgCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", slug: "" });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const slugify = (value) => {
    const cleaned = value.toLowerCase().trim();
    if (!cleaned) return "";
    const slug = cleaned.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    return slug || "org";
  };

  const resolveErrorMessage = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    if (status === 422) {
      if (Array.isArray(detail)) {
        const hasSlugError = detail.some(
          (item) => Array.isArray(item?.loc) && item.loc.includes("slug")
        );
        if (hasSlugError) {
          return "Identificador invalido. Use apenas letras minusculas, numeros e hifens, sem espacos.";
        }
        const hasNameError = detail.some(
          (item) => Array.isArray(item?.loc) && item.loc.includes("name")
        );
        if (hasNameError) {
          return "Nome da organizacao e obrigatorio.";
        }
      }
      return "Dados invalidos. Verifique os campos e tente novamente.";
    }

    if (status === 409) {
      return "Ja existe uma organizacao com este identificador. Escolha outro.";
    }

    if (status === 400 && typeof detail === "string" && detail.toLowerCase().includes("reserved")) {
      return "Este identificador esta reservado. Escolha outro.";
    }

    if (typeof detail === "string") {
      return detail;
    }

    return "Erro ao criar organizacao";
  };

  const trimmedSlug = form.slug?.trim() || "";
  const previewSlug = trimmedSlug || slugify(form.name);
  const bookingUrl = `${baseUrl}/${previewSlug || "<identificador>"}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        slug: trimmedSlug ? trimmedSlug : null,
      };
      const org = await createOrganization(payload);
      navigate(`/admin/${org.slug}`);
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-white mb-6 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gold mb-3">
            Nova organizacao
          </h1>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-card border border-border rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="org-name" className="block text-sm font-medium text-white">
                Nome *
              </label>
              <input
                id="org-name"
                value={form.name}
                onChange={set("name")}
                required
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="org-slug" className="block text-sm font-medium text-white">
                Identificador da loja (opcional)
              </label>
              <input
                id="org-slug"
                value={form.slug}
                onChange={set("slug")}
                placeholder="ex: galeria-men-salon"
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
              <div className="space-y-1 text-xs text-muted">
                <p>Define o URL de marcacoes para clientes.</p>
                <p>Regras: apenas letras minusculas, numeros e hifens, sem espacos.</p>
                <p>
                  URL de marcacoes:{" "}
                  <code className="px-1.5 py-0.5 bg-background rounded text-gold">
                    {bookingUrl}
                  </code>
                </p>
                <p>Se deixar vazio, o sistema gera automaticamente a partir do nome.</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={submitting}>
                {submitting ? "A criar..." : "Criar organizacao"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin")}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
